import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useSwipe } from '../../hooks/useSwipe.js';
import { RATINGS } from '../../scheduler/fsrs.js';
import { haptic } from '../../app/settingsStore.js';
import styles from './Study.module.scss';

/** The card itself. Tap to reveal; once revealed, swipe to answer. */
export default function SwipeCard({
  revealed,
  swipeMap,
  swipeEnabled,
  onReveal,
  onSwipeRate,
  flag,
  label,
  labelTone,
  tapHint,
  children,
  cardKey,
}) {
  const contentRef = useRef(null);
  const [scrollable, setScrollable] = useState(false);
  const [leaving, setLeaving] = useState(null);
  const lastPending = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;
    const check = () => setScrollable(el.scrollHeight > el.clientHeight + 2);
    check();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(check) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [revealed, cardKey]);

  const active = revealed && swipeEnabled;
  const { drag, pending, handlers } = useSwipe({
    enabled: active,
    scrollRef: contentRef,
    onTap: () => {
      if (!revealed) onReveal();
    },
    onSwipe: (dir) => {
      const rating = swipeMap[dir];
      if (!rating) return;
      setLeaving(dir);
      setTimeout(() => onSwipeRate(rating), 160);
    },
  });

  useEffect(() => {
    if (pending && pending !== lastPending.current) haptic(4);
    lastPending.current = pending;
  }, [pending]);

  const pendingRating = pending ? RATINGS.find((r) => r.value === swipeMap[pending]) : null;
  const style = {
    touchAction: active ? 'none' : undefined,
    ...(drag.active
      ? { transform: `translate(${drag.x}px, ${drag.y}px) rotate(${drag.x / 24}deg)`, transition: 'none' }
      : null),
  };

  return (
    <div
      className={clsx(
        styles.card,
        revealed && styles.revealed,
        leaving && styles[`leave_${leaving}`],
        active && styles.swipeable,
        scrollable && styles.scrollable,
        pendingRating && styles[`tint_${pendingRating.key}`],
      )}
      style={style}
      {...handlers}
      role={revealed ? undefined : 'button'}
      tabIndex={revealed ? undefined : 0}
      aria-label={revealed ? undefined : 'Show answer'}
    >
      <div className={styles.cardTop}>
        {label ? <span className={clsx(styles.stateChip, styles[`chip_${labelTone}`])}>{label}</span> : <span />}
        {flag ? <span className={styles.flag} style={{ color: flag }} aria-label="Flagged" /> : null}
      </div>
      <div ref={contentRef} className={clsx(styles.cardContent, 'rich')}>
        {children}
      </div>
      {pendingRating ? (
        <div className={clsx(styles.swipeHint, styles[pendingRating.key])}>{pendingRating.label}</div>
      ) : null}
      {!revealed && tapHint ? <p className={styles.tapHint}>{tapHint}</p> : null}
    </div>
  );
}
